
import React, { useState, useEffect } from 'react';
import { AuthScreen } from '../types';
import { useDashboardStore } from '../src/stores/dashboard.store';
import { useLogout } from '../src/hooks/useAuth';
import { useAuthStore } from '../src/stores/auth.store';
import Sidebar from '../components/Sidebar';
import MobileBottomNav from '../components/MobileBottomNav';
import LiveMap from '../components/LiveMap';
import MethaneTrends from '../components/MethaneTrends';
import MethaneConverter from '../components/MethaneConverter';
import DataComparison from '../components/DataComparison';
import DataTabs from '../components/DataTabs';
import ManageData from '../components/ManageData';
import AlertsDashboard from '../components/AlertsDashboard';
import FilterPanel, { DEFAULT_FILTERS, type MapFilters } from '../components/FilterPanel';
import UserManagement from '../components/UserManagement';
import SettingsPage from '../components/SettingsPage';
import DashboardHome from '../components/DashboardHome';
import FieldDataForm from '../components/FieldDataForm';
import ScreenGuide from '../components/ScreenGuide';
import GuidedTour from '../components/GuidedTour';
import ErrorBoundary from '../components/ErrorBoundary';
import Docs from './docs/Docs';
import { getDocBySlug } from './docs/manifest';
import DataFeedsPage from '../components/DataFeedsPage';
import { usersApi } from '../src/api/users.api';

interface DashboardProps {
  onNavigate: (screen: AuthScreen) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { activeView, sidebarCollapsed, toggleSidebar, isFilterOpen, setFilterOpen, darkMode, toggleDarkMode, setActiveView } = useDashboardStore();
  const logoutMutation = useLogout();
  const [mapFilters, setMapFilters] = useState<MapFilters>(DEFAULT_FILTERS);
  const user = useAuthStore((s) => s.user);
  const syncCurrentUserName = useAuthStore((s) => s.syncCurrentUserName);
  const isFacilityOwner = user?.role === 'facility_owner';
  const returnFromDocs = React.useRef<typeof activeView>('LIVE_MAP');
  const previousView = React.useRef(activeView);
  const tourStorageKey = `nogiet-guided-tour-v1:${user?.id ?? 'guest'}`;
  const [tourOpen, setTourOpen] = useState(() => user?.id && !new URLSearchParams(window.location.search).get('guide') ? localStorage.getItem(`nogiet-guided-tour-v1:${user.id}`) !== 'done' : false);
  const [tourViewReady, setTourViewReady] = useState(false);
  const displayedView = tourOpen && !tourViewReady ? (isFacilityOwner ? 'FIELD_DATA' : 'DASHBOARD_HOME') : activeView;
  const closeTour = React.useCallback(() => {
    localStorage.setItem(tourStorageKey, 'done');
    setTourOpen(false);
  }, [tourStorageKey]);
  const startTour = React.useCallback(() => { setTourViewReady(false); setTourOpen(true); }, []);
  const showTourView = React.useCallback((view: typeof activeView) => {
    setTourViewReady(true);
    setActiveView(view);
  }, [setActiveView]);

  useEffect(() => {
    if (!user?.id || (user.role !== 'super_admin' && user.role !== 'admin')) return;
    let active = true;
    usersApi.getById(user.id).then((response) => {
      if (active) syncCurrentUserName(response.data.id, response.data.fullName);
    }).catch(() => {
      // Keep the cached name if the profile cannot be loaded.
    });
    return () => { active = false; };
  }, [user?.id, user?.role, syncCurrentUserName]);

  useEffect(() => {
    if (activeView === 'DOCS' && previousView.current !== 'DOCS') returnFromDocs.current = previousView.current;
    previousView.current = activeView;
  }, [activeView]);

  useEffect(() => {
    const guide = new URLSearchParams(window.location.search).get('guide');
    if (guide && getDocBySlug(guide)) setActiveView('DOCS');
  }, [setActiveView]);


  useEffect(() => {
    if (isFacilityOwner && activeView !== 'FIELD_DATA' && activeView !== 'METHANE_CONVERTER' && activeView !== 'DOCS') {
      setActiveView('FIELD_DATA');
    }
  }, [isFacilityOwner, activeView, setActiveView]);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSettled: () => onNavigate(AuthScreen.LOGIN),
    });
  };

  const renderContent = () => {
    let content: React.ReactNode;
    let label = 'Screen';

    switch (displayedView) {
      case 'DASHBOARD_HOME':
        content = <DashboardHome darkMode={darkMode} onNavigate={(v) => setActiveView(v as any)} />;
        label = 'Dashboard';
        break;
      case 'LIVE_MAP':
        content = <LiveMap onOpenFilters={() => setFilterOpen(true)} darkMode={darkMode} onNavigateAlerts={() => setActiveView('ALERTS')} onNavigateMethaneTrends={() => setActiveView('METHANE_TRENDS')} filters={mapFilters} />;
        label = 'Live Map';
        break;
      case 'METHANE_TRENDS':
        content = <MethaneTrends darkMode={darkMode} onClose={() => setActiveView('LIVE_MAP')} />;
        label = 'Methane Trends';
        break;
      case 'METHANE_CONVERTER':
        content = <MethaneConverter darkMode={darkMode} />;
        label = 'Methane Converter';
        break;
      case 'DATA_COMPARISON':
        content = <DataComparison darkMode={darkMode} />;
        label = 'Data Comparison';
        break;
      case 'DATA_TABS':
        content = <DataTabs darkMode={darkMode} />;
        label = 'Data Tabs';
        break;
      case 'MANAGE_DATA':
        content = <ManageData darkMode={darkMode} onNavigateAlerts={() => setActiveView('ALERTS')} />;
        label = 'Manage Data';
        break;
      case 'ALERTS':
        content = <AlertsDashboard darkMode={darkMode} />;
        label = 'Alerts';
        break;
      case 'FIELD_DATA':
        content = <FieldDataForm darkMode={darkMode} />;
        label = 'Field Data';
        break;
      case 'USER_MANAGEMENT':
        content = <UserManagement darkMode={darkMode} />;
        label = 'User Management';
        break;
      case 'SETTINGS':
        content = <SettingsPage darkMode={darkMode} onClose={() => setActiveView('DASHBOARD_HOME')} onStartTour={startTour} />;
        label = 'Settings';
        break;
      case 'DATA_FEEDS':
        content = user?.role === 'super_admin' ? <DataFeedsPage darkMode={darkMode} onClose={() => setActiveView('DASHBOARD_HOME')} /> : <DashboardHome darkMode={darkMode} onNavigate={(v) => setActiveView(v as any)} />;
        label = 'Data Feeds';
        break;
      case 'DOCS':
        content = <Docs darkMode={darkMode} onClose={() => setActiveView(returnFromDocs.current)} />;
        label = 'Documentation';
        break;
      default:
        content = <DashboardHome darkMode={darkMode} onNavigate={(v) => setActiveView(v as any)} />;
        label = 'Dashboard';
    }

    return (
      <ErrorBoundary darkMode={darkMode} screenName={label}>
        {content}
      </ErrorBoundary>
    );
  };

  return (
    // Breakpoint policy: the desktop sidebar appears at `lg:` (1024px+). Below
    // that we render the mobile bottom-nav, even on tablets / small laptops.
    // We picked `lg` because at `md` (768px) the 256px sidebar + 320px summary
    // card + Mapbox UI overflowed small-laptop viewports (1366×768 zoomed to
    // 125% effective ≈ 1093×614): the very screen the client was on.
    <div className={`flex h-screen transition-colors duration-300 ${darkMode ? 'bg-[#0b0e14]' : 'bg-gray-50'} overflow-hidden`}>
      {activeView !== 'DOCS' && <div className="hidden lg:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
          activeView={activeView}
          onViewChange={setActiveView}
          onLogout={handleLogout}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
        />
      </div>}
      <main className="flex-1 relative overflow-hidden flex flex-col pb-[env(safe-area-inset-bottom)] lg:pb-0">
        <div data-tour-screen={displayedView} className={`flex-1 overflow-hidden flex flex-col ${displayedView !== 'LIVE_MAP' && displayedView !== 'DOCS' ? 'pb-16 lg:pb-0' : ''}`}>
          {renderContent()}
        </div>
        {isFilterOpen && <FilterPanel onClose={() => setFilterOpen(false)} darkMode={darkMode} filters={mapFilters} onApply={setMapFilters} />}
        <ScreenGuide darkMode={darkMode} screenKey={activeView} onStartTour={startTour} />
      </main>
      {tourOpen && <GuidedTour darkMode={darkMode} isFacilityOwner={isFacilityOwner} onViewChange={showTourView} onClose={closeTour} />}
      {activeView !== 'DOCS' && <div className="lg:hidden">
        <MobileBottomNav
          activeView={activeView}
          onViewChange={setActiveView}
          onLogout={handleLogout}
          darkMode={darkMode}
        />
      </div>}
    </div>
  );
};

export default Dashboard;
